import { signalStoreFeature, withState } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { authEvents } from './events';

export interface TerritoryAccess {
  id: string;
  name: string;
  role: 'TERRITORY_ADMIN' | 'MEMBER';
}

export interface AuthState {
  /** Cognito user attributes — flat map matching GetUser response. */
  sub: string;
  /** `custom:user_id` — the caller's users.id (≠ Cognito sub), used to filter "mine" (e.g. tasks). */
  userId: string;
  email: string;
  given_name: string;
  family_name: string;
  email_verified: string;
  /** Differentiates portal access: see Role enum for all values. */
  'custom:role': string;
  /** Null for corporate users (cross-territory); set for territory-scoped users. */
  'custom:territory_id': string;
  /** `custom:portal` claim — CORPORATE | TERRITORY | SUB | CLIENT. */
  portal: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Active territory context — corporate/NAM users switch this from the TopBar. */
  activeTerritoryId: string | null;
  /** All territories this user can access (empty for corporate users who see all). */
  territories: TerritoryAccess[];
  /** For National Account Managers: account IDs they manage cross-territory. */
  managedAccountIds: string[] | null;
  /** Territories where this user holds a Sub Proxy grant (drives `hasSubProxyGrant`). */
  subProxyTerritoryIds: string[];
  /** MFA challenge session data (set when Cognito returns SOFTWARE_TOKEN_MFA challenge). */
  mfaChallengeSession: string | null;
  mfaChallengeUsername: string | null;
  mfaEnabled: boolean | null;
  /** Show MFA recommendation banner for users without 2FA. */
  showMfaRecommendation: boolean;
  /** True while a NEW_PASSWORD_REQUIRED challenge is pending (SRP keeps no portable session). */
  awaitingNewPassword: boolean;
  /** NEW_PASSWORD_REQUIRED challenge session (legacy SDK flow; unused under SRP). */
  newPasswordSession: string | null;
  newPasswordUsername: string | null;
  /** `custom:mfa_required` — internal staff must enroll/use TOTP. */
  mfaRequired: boolean;
  /** Base32 TOTP secret during enrollment (shown as QR/manual key on the setup page). */
  mfaSecretCode: string | null;
}

/** Maps the CSV `custom:territory_ids` claim (already split) to TerritoryAccess rows. */
function territoriesFromIds(ids: string[] | undefined): TerritoryAccess[] {
  return (ids ?? []).map((id) => ({ id, name: id, role: 'MEMBER' as const }));
}

export const initialAuthState: AuthState = {
  sub: '',
  userId: '',
  email: '',
  given_name: '',
  family_name: '',
  email_verified: '',
  'custom:role': '',
  'custom:territory_id': '',
  portal: '',
  isAuthenticated: false,
  isLoading: false,
  error: null,
  activeTerritoryId: null,
  territories: [],
  managedAccountIds: null,
  subProxyTerritoryIds: [],
  mfaChallengeSession: null,
  mfaChallengeUsername: null,
  mfaEnabled: null,
  showMfaRecommendation: false,
  awaitingNewPassword: false,
  newPasswordSession: null,
  newPasswordUsername: null,
  mfaRequired: false,
  mfaSecretCode: null,
};

export function withAuthReducer() {
  return signalStoreFeature(
    withState<AuthState>(initialAuthState),
    withReducer(
      on(authEvents.setLoading, ({ payload: isLoading }) => ({ isLoading })),
      on(authEvents.clearError, () => ({ error: null })),
      on(authEvents.loginFailed, ({ payload: error }) => ({ error, isLoading: false })),

      on(authEvents.getUserSuccess, ({ payload: user }) => ({
        sub: user.sub,
        userId: user.userId ?? '',
        email: user.email,
        given_name: user.givenName,
        family_name: user.familyName,
        email_verified: user.emailVerified,
        'custom:role': user['custom:role'],
        'custom:territory_id': user['custom:territory_id'],
        portal: user.portal ?? '',
        territories: territoriesFromIds(user.territoryIds),
        managedAccountIds: user.managedAccountIds ?? null,
        subProxyTerritoryIds: user.subProxyTerritoryIds ?? [],
        mfaRequired: user.mfaRequired ?? false,
        // Both operands come from claims the token does not carry, so in production this is
        // ALWAYS null and only the switcher (`setTerritoryContext`, below) ever sets it. Kept
        // because it is what gives dev mock auth a starting territory; never rely on it for a
        // value that must reach the wire.
        activeTerritoryId: user['custom:territory_id'] || user.territoryIds?.[0] || null,
        error: null,
      })),

      on(authEvents.loginSuccess, () => ({
        isAuthenticated: true,
        isLoading: false,
        error: null,
        mfaChallengeSession: null,
        mfaChallengeUsername: null,
        awaitingNewPassword: false,
        newPasswordSession: null,
        newPasswordUsername: null,
      })),

      on(authEvents.newPasswordRequired, ({ payload }) => ({
        awaitingNewPassword: true,
        newPasswordSession: payload.session,
        newPasswordUsername: payload.username,
        isLoading: false,
        error: null,
      })),

      on(authEvents.startMfaSetup, () => ({ isLoading: true, error: null, mfaSecretCode: null })),
      on(authEvents.mfaSecretLoaded, ({ payload: mfaSecretCode }) => ({ mfaSecretCode, isLoading: false })),
      on(authEvents.submitMfaSetup, () => ({ isLoading: true, error: null })),

      on(authEvents.logout, () => ({ ...initialAuthState })),
      on(authEvents.logoutSuccess, () => ({ ...initialAuthState })),

      on(authEvents.mfaChallengeRequired, ({ payload }) => ({
        mfaChallengeSession: payload.session,
        mfaChallengeUsername: payload.username,
        isLoading: false,
      })),

      on(authEvents.mfaStatusLoaded, ({ payload }) => ({
        mfaEnabled: payload.enabled,
        showMfaRecommendation: !payload.enabled,
      })),

      // ⚠️ Never dispatched anywhere in the repo — `territoriesLoaded` and
      // `managedAccountIdsLoaded` are both declared, both reduced, and neither is ever emitted.
      // They read as live fallbacks for `activeTerritoryId` and are not. Either wire them or
      // delete them; leaving them is what makes the null above look impossible.
      on(authEvents.territoriesLoaded, ({ payload: territories }) => ({
        territories,
        activeTerritoryId: territories[0]?.id ?? null,
      })),

      on(authEvents.managedAccountIdsLoaded, ({ payload: managedAccountIds }) => ({
        managedAccountIds,
      })),

      on(authEvents.setTerritoryContext, ({ payload: activeTerritoryId }) => ({
        activeTerritoryId,
      })),

      on(authEvents.restoreSessionSuccess, ({ payload: user }) => ({
        sub: user.sub,
        userId: user.userId ?? '',
        email: user.email,
        given_name: user.givenName,
        family_name: user.familyName,
        email_verified: user.emailVerified,
        'custom:role': user['custom:role'],
        'custom:territory_id': user['custom:territory_id'],
        portal: user.portal ?? '',
        territories: territoriesFromIds(user.territoryIds),
        managedAccountIds: user.managedAccountIds ?? null,
        subProxyTerritoryIds: user.subProxyTerritoryIds ?? [],
        mfaRequired: user.mfaRequired ?? false,
        isAuthenticated: true,
        activeTerritoryId: user['custom:territory_id'] || user.territoryIds?.[0] || null,
      })),

      on(authEvents.restoreSessionFailed, () => ({ ...initialAuthState })),
    ),
  );
}
